import React, {useEffect, useRef, useState} from 'react';
import {Text, TextInput, TouchableOpacity, View} from 'react-native';
import i18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import {
  accentColor,
  dangerColor,
  groupBg,
  groupBorderColor,
  groupRadius,
  textColor,
  textInputBg,
  textInputHeight,
  textInputRadius,
  textInputText,
  textMutedColor,
  toggleOffColor,
  touchButtonHeight,
  mobileInputFontSize
} from '../../../style/adminTools';
import {sheetRadius, sheetScrimColor} from '../../../style/adminTenantGrid';

const t = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

const enterBelongsToDialog = target => {
  if (!target || !target.getAttribute) return true;
  if (target.getAttribute('role') === 'button') return false;
  return !['INPUT', 'TEXTAREA'].includes(target.tagName);
};

const TenantActionDialog = ({
  message,
  confirmLabel,
  danger,
  withInput,
  inputMatches,
  busy,
  sheet,
  onConfirm,
  onCancel,
  testID = 'tenantActionDialog'
}) => {
  const [value, setValue] = useState('');
  const canConfirm = !busy && (!withInput || inputMatches(value));
  const latest = useRef({});
  latest.current = {canConfirm, value, onConfirm, onCancel};

  useEffect(() => {
    const onKeyDown = event => {
      const {
        canConfirm: ready,
        value: typed,
        onConfirm: confirm,
        onCancel: cancel
      } = latest.current;
      if (event.key === 'Escape') {
        cancel();
      } else if (
        event.key === 'Enter' &&
        ready &&
        enterBelongsToDialog(event.target)
      ) {
        confirm(typed);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <View
      testID={testID}
      accessibilityRole="dialog"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: sheet ? 'flex-end' : 'center',
        alignItems: 'center',
        backgroundColor: sheetScrimColor,
        zIndex: 5000
      }}
    >
      <View
        testID={sheet ? 'tenantActionSheet' : undefined}
        style={{
          width: sheet ? '100%' : '90%',
          maxWidth: sheet ? undefined : 440,
          padding: 24,
          backgroundColor: groupBg,
          borderWidth: 1,
          borderColor: groupBorderColor,
          borderRadius: groupRadius,
          ...(sheet
            ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: sheetRadius,
                borderTopRightRadius: sheetRadius
              }
            : {})
        }}
      >
        <Text
          testID="tenantDialogMessage"
          style={{
            fontFamily: fontFamily.font500,
            fontSize: 15,
            lineHeight: 22,
            color: textColor
          }}
        >
          {message}
        </Text>
        {withInput ? (
          <TextInput
            testID="tenantDialogInput"
            value={value}
            onChangeText={setValue}
            onSubmitEditing={() => {
              const {canConfirm: ready, value: typed, onConfirm: confirm} =
                latest.current;
              if (ready) confirm(typed);
            }}
            autoFocus
            placeholderTextColor={textMutedColor}
            style={{
              height: sheet ? touchButtonHeight : textInputHeight,
              marginTop: 16,
              paddingHorizontal: 12,
              backgroundColor: textInputBg,
              borderRadius: textInputRadius,
              ...textInputText,
              ...(sheet ? {fontSize: mobileInputFontSize} : {})
            }}
          />
        ) : null}
        <View
          style={{
            flexDirection: sheet ? 'column-reverse' : 'row',
            justifyContent: 'flex-end',
            marginTop: 24
          }}
        >
          <TouchableOpacity
            testID="tenantDialogCancel"
            accessibilityRole="button"
            onPress={onCancel}
            style={{
              minHeight: sheet ? touchButtonHeight : 44,
              marginRight: sheet ? 0 : 10,
              marginTop: sheet ? 16 : 0,
              alignItems: sheet ? 'center' : undefined,
              paddingHorizontal: 18,
              justifyContent: 'center',
              borderRadius: 7,
              borderWidth: 2,
              borderColor: toggleOffColor
            }}
          >
            <Text
              style={{
                fontFamily: fontFamily.font700,
                fontSize: 14,
                color: textColor
              }}
            >
              {i18n.t('productGrid.cancel')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="tenantDialogConfirm"
            accessibilityRole="button"
            accessibilityState={{disabled: !canConfirm}}
            disabled={!canConfirm}
            onPress={() => canConfirm && onConfirm(value)}
            style={{
              minHeight: sheet ? touchButtonHeight : 44,
              paddingHorizontal: 18,
              justifyContent: 'center',
              alignItems: sheet ? 'center' : undefined,
              borderRadius: 7,
              backgroundColor: danger ? dangerColor : accentColor,
              opacity: canConfirm ? 1 : 0.4
            }}
          >
            <Text
              style={{
                fontFamily: fontFamily.font700,
                fontSize: 14,
                color: textColor
              }}
            >
              {confirmLabel}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default TenantActionDialog;
